//core
import React, {useCallback, useContext, useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import { Scrollbars } from "react-custom-scrollbars";
import { useSelector } from "react-redux";
import { useLocalStorage } from "usehooks-ts";
import arePropsEqual from "react-fast-compare";
import {
  Box,
  useMediaQuery,
} from "@mui/material";
import format from "date-fns/format";
import {endOfDay, startOfDay} from "date-fns";

//styles
import { useStyles } from "./styles";

//hooks
import useDidMountEffect from "../../../../../../../customHooks/useDidMountEffect";
import { usePartners } from "../../../../../../../store/admin/partners/usePartners";
import { useFields } from "../../../../../../../store/common/fields/useFields";
import { getObjIds } from "../../../../../../../helpers/getIds";
import {getDataIds} from "../../../../../Reports/utils";

//context
import { GlobalContext } from "../../../../../../../context/GlobalContext";

//constants
import { favoriteFilterKeys, statusOptions } from "../../../../constants";

//components
import MuiAutocompleteSelect from "../../../../../../UI/Fields/MuiAutocompleteSelect";
import FavoriteCheckboxField from "../../../../../../UI/Fields/FavoriteCheckboxField";
import { IFlatFeeFilterDataType } from "../../../../types";
import MuiAutocompleteSelectLazy from "../../../../../../UI/Fields/MuiAutocompleteSelectLazy";
import Button from "../../../../../../UI/Buttons/Button";
import MuiTwiceDateRangePicker from "../../../../../../UI/Fields/MuiTwiceDateRangePicker";


interface IFilterProps {
  initialFilter: IFlatFeeFilterDataType;
  activeField: string;
  setUpdateFilter: (data: any) => void;
  setToggleDrawer: (data: boolean) => void;
  setActiveField: (field: string) => void;
}

const Filter: React.FC<IFilterProps> = ({
  initialFilter,
  activeField,
  setUpdateFilter,
  setToggleDrawer,
  setActiveField,
}): JSX.Element => {
  const { t } = useTranslation();
  const styles = useStyles();
  const mobile = useMediaQuery('(max-width:767px)');

  const context = useContext(GlobalContext);

	const { getAffiliates } = usePartners();
	const { getTrackers, getBrands } = useFields();

  const [filter, setFilter] = useState<IFlatFeeFilterDataType>(context.admFlatFeeFilter);

	const [startDate, setStartDate] = useState<any>(context.admFlatFeeFilter.sent_from || '');
	const [endDate, setEndDate] = useState<any>(context.admFlatFeeFilter.sent_to || '');

  //favorite fields state
	const [favAdmFFStatuses, setFavAdmFFStatuses] = useLocalStorage<boolean>(favoriteFilterKeys.statuses, false);
	const [favAdmFFAffiliateIds, setFavAdmFFAffiliateIds] = useLocalStorage<boolean>(favoriteFilterKeys.affiliateIds, false);
	const [favAdmFFTrackerIds, setFavAdmFFTrackerIds] = useLocalStorage<boolean>(favoriteFilterKeys.trackerIds, false);
	const [favAdmFFBrandCodes, setFavAdmFFBrandCodes] = useLocalStorage<boolean>(favoriteFilterKeys.brandCodes, false);

  useDidMountEffect(() => {
    context.setAdmFlatFeeFilter(filter);
  }, [filter]);

  const handleApplyFilter = useCallback(() => {
    setUpdateFilter(filter);
    setToggleDrawer(false);
  }, [filter]);

	const handleChangeDates = (start: Date | null, end: Date | null) => {
		setStartDate(!!start ? start : '');
		setEndDate(!!end ? end : '');

		setFilter((prevState: any) => {
			return {
				...prevState,
				sent_from: !!start ? format(startOfDay(new Date(start)), "yyyy-MM-dd HH:mm:ss") : '',
				sent_to: !!end ? format(endOfDay(new Date(end)), "yyyy-MM-dd HH:mm:ss") : '',
			}
		});
	};

	const fieldAffiliateIdsPayload = () => {
		return {
			chiefIds: null,
			departmentIds: null,
			status: 'approved'
		}
	};

	const fieldTrackerIdsPayload = () => {
		return {
			per_page: 100,
			...(!!filter?.affiliateIds.length ? { affiliateIds: getDataIds(filter.affiliateIds) } : {}),
		}
	};

  return (
    <>
      {/*{!!dataFilters && <>*/}
        <Box className={styles.wrapper}>
          <Scrollbars
            className={styles.customScroll}
            style={{ overflow: "hidden" }}
            hideTracksWhenNotNeeded={true}
            renderView={props => <div {...props} className="view"/>}
            renderTrackHorizontal={props => <div {...props} className="track-horizontal"/>}
            renderTrackVertical={props => <div {...props} className="track-vertical"/>}
          >
	          <Box className={`${styles.box} noMargin`}>
		          <Box className={`${styles.fieldWrapperPadding} `}>
			          <MuiTwiceDateRangePicker
				          label={'admin.reports.filter.date'}
				          startLabel={'admin.reports.filter.from'}
				          endLabel={'admin.reports.filter.to'}
				          startDate={startDate}
				          endDate={endDate}
				          onChange={handleChangeDates}
				          maxDate={null}
			          />
		          </Box>
	          </Box>
	          {/*affiliateIds*/}
	          <Box className={styles.box}>
		          <Box className={styles.fieldWrapper}>
			          {!mobile && (
				          <Box className={styles.fieldFavorite}>
					          <FavoriteCheckboxField
						          checked={favAdmFFAffiliateIds}
						          setChecked={setFavAdmFFAffiliateIds}
						          fieldName={t(`admin.flatFee.filter.affiliateIds`)}
					          />
				          </Box>
			          )}
			          <Box className={styles.field}>
				          <MuiAutocompleteSelectLazy
					          keyValue="value"
					          keyId="id"
					          id="affiliateIds"
					          name="affiliateIds"
					          label={t(`admin.flatFee.filter.affiliateIds`)}
					          value={filter.affiliateIds}
					          multiple={true}
					          showSelectAll={false}
					          disabled={false}
					          getOptions={getAffiliates}
					          payloadData={fieldAffiliateIdsPayload()}
					          labelWithId={true}
					          onChange={setFilter}
					          disableCloseOnSelect={true}
					          autoFocus={activeField === 'affiliateIds'}
					          showLabelCounter={true}
					          showNameInPayload={true}
				          />
			          </Box>
		          </Box>
	          </Box>
	          {/*trackerIds*/}
	          <Box className={styles.box}>
		          <Box className={styles.fieldWrapper}>
			          {!mobile && (
				          <Box className={styles.fieldFavorite}>
					          <FavoriteCheckboxField
						          checked={favAdmFFTrackerIds}
						          setChecked={setFavAdmFFTrackerIds}
						          fieldName={t(`admin.flatFee.filter.trackerIds`)}
					          />
				          </Box>
			          )}
			          <Box className={styles.field}>
				          <MuiAutocompleteSelectLazy
					          keyValue="value"
					          keyId="id"
					          id="trackerIds"
					          name="trackerIds"
					          label={t(`admin.flatFee.filter.trackerIds`)}
					          value={filter.trackerIds}
					          multiple={true}
					          showSelectAll={false}
					          disabled={false}
					          getOptions={getTrackers}
					          payloadData={fieldTrackerIdsPayload()}
					          labelWithId={false}
					          onChange={setFilter}
					          disableCloseOnSelect={true}
					          autoFocus={activeField === 'trackerIds'}
					          showLabelCounter={true}
					          showNameInPayload={true}
				          />
			          </Box>
		          </Box>
	          </Box>
	          {/*brandCodes*/}
	          <Box className={styles.box}>
		          <Box className={styles.fieldWrapper}>
			          {!mobile && (
				          <Box className={styles.fieldFavorite}>
					          <FavoriteCheckboxField
						          checked={favAdmFFBrandCodes}
						          setChecked={setFavAdmFFBrandCodes}
						          fieldName={t("admin.flatFee.filter.brandCodes")}
					          />
				          </Box>
			          )}
			          <Box className={styles.field}>
				          <MuiAutocompleteSelectLazy
					          keyValue="value"
					          keyId="id"
					          id="brandCodes"
					          name="brandCodes"
					          label={t("admin.flatFee.filter.brandCodes")}
					          value={filter.brandCodes}
					          multiple={true}
					          showSelectAll={false}
					          disabled={false}
					          getOptions={getBrands}
					          payloadData={{}}
					          labelWithId={true}
					          onChange={setFilter}
					          disableCloseOnSelect={true}
					          autoFocus={activeField === 'brandCodes'}
					          showLabelCounter={true}
					          showNameInPayload={true}
				          />
			          </Box>
		          </Box>
	          </Box>
	          {/*statuses*/}
	          <Box className={styles.box}>
		          <Box className={styles.fieldWrapper}>
			          {!mobile && (
				          <Box className={styles.fieldFavorite}>
					          <FavoriteCheckboxField
						          checked={favAdmFFStatuses}
						          setChecked={setFavAdmFFStatuses}
						          fieldName={t("admin.flatFee.filter.statuses")}
					          />
				          </Box>
			          )}
			          <Box className={styles.field}>
				          <MuiAutocompleteSelect
					          keyValue="value"
					          keyId="id"
					          id="statuses"
					          name="statuses"
					          label={t("admin.flatFee.filter.statuses")}
					          value={filter.statuses}
					          multiple={true}
					          showSelectAll={false}
					          disabled={false}
					          staticOptions={statusOptions}
					          onChange={setFilter}
					          disableCloseOnSelect={true}
					          autoFocus={activeField === 'statuses'}
					          showLabelCounter={true}
	                  prefix="admin.flatFee.form.options.statuses."
				          />
			          </Box>
		          </Box>
	          </Box>

          </Scrollbars>
        </Box>

        <Box className={styles.actions}>
          <Box className={styles.actionsWrapper}>
            <Button
              gradient
              className={styles.button}
              fullWidth
              disableElevation
              type="submit"
              variant="contained"
              size="large"
              onClick={handleApplyFilter}
              disabled={arePropsEqual(initialFilter, filter)}
            >
              {t("common.buttons.apply")}
            </Button>
            <Button
              className={styles.button}
              color="secondary"
              disableElevation
              type="button"
              variant="outlined"
              size="large"
              onClick={() => setToggleDrawer(false)}
            >
              {t("common.buttons.cancel")}
            </Button>
          </Box>
        </Box>
      {/*</>}*/}
    </>
  );
};

export default Filter;
